/**
 * CW-SF-M5 — Shared utilities for Salesforce dashboard widgets.
 *
 * All 4 widgets share:
 *   - The connection-state envelope ({connection_state, last_synced_at, stale}).
 *   - A "Connect Salesforce" empty-state CTA.
 *   - A "Last synced N min ago" footer + Reconnect banner above stale data.
 *   - Currency / countdown formatters.
 *
 * Cross-References:
 *   - sprint-cw-sf-m5-widgets.md TKT-1
 *   - services/widgetRegistry.ts (useAutoRefresh hook)
 */

export interface SfWidgetEnvelope {
  connection_state: 'active' | 'degraded' | 'missing';
  last_synced_at: string | null;
  stale: boolean;
}

export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toFixed(0)}`;
}

export function daysUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null;
  const now = Date.now();
  // Parse YYYY-MM-DD as UTC noon to avoid TZ drift.
  const target = new Date(`${isoDate}T12:00:00Z`).getTime();
  if (Number.isNaN(target)) return null;
  return Math.round((target - now) / (24 * 60 * 60 * 1000));
}

export function formatRelativeMinutes(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * CW-SF-R1 TKT-7 — build a widget endpoint URL with role-zone filters.
 *
 * Reads the well-known config keys placed by the admin region editor
 * (account_list_view_ids[], owner_scope) plus the private `_role_id`
 * injected by `RoleZone.tsx`, and serializes them as query params the
 * backend `_resolve_r1_filters` helper expects.
 *
 * Behavior outside the role zone is identical to a bare path —
 * config has no list view IDs / owner_scope, so no query params get
 * appended.
 */
export function buildSfWidgetUrl(
  path: string,
  config?: Record<string, unknown>,
): string {
  if (!config) return path;
  const params = new URLSearchParams();
  const lvIds = config.account_list_view_ids;
  if (Array.isArray(lvIds) && lvIds.length > 0) {
    params.set('list_view_ids', lvIds.map(String).join(','));
  }
  const ownerScope = config.owner_scope;
  if (typeof ownerScope === 'string' && ownerScope !== 'unscoped' && ownerScope.length > 0) {
    params.set('owner_scope', ownerScope);
  }
  const roleId = config._role_id;
  if (typeof roleId === 'string' && roleId.length > 0) {
    params.set('role_id', roleId);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
